import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteAction } from 'state/common-form/infos/infos.model';
import type { IRegulatoryRejection } from 'state/regulatoryDocuments';
import type { Collection } from 'state/utils/types';

export type PriceEnhancementMode = 'None' | 'UpfrontFee' | 'Reoffer' | 'Premium';

export type TradeQuoteAction = QuoteAction &
  Readonly<{
    priceEnhancement: PriceEnhancementMode;
  }>;

export type TradeStatus = 'Done' | 'Failed' | 'Pending' | 'Rejected' | 'Timeout';

export type PriceEnhancement = Readonly<{
  issuePrice: number;
  tradingOffer: number;
  xMargin: number;
}>;

export type PriceEnhancementField = keyof PriceEnhancement;

export type NextTradeAuthorizationStatus =
  | 'Granted'
  | 'Denied'
  | 'DerogationRequired'
  | 'Pending'
  | 'Failed'
  | 'Timeout';

export type NextTradeAuthorizationResult = Readonly<{
  quoteId: string;
  expirationDelayInSeconds: number;
  rejections: readonly IRegulatoryRejection[];
  status: NextTradeAuthorizationStatus;
  createdOn?: string;
  expiresOn?: string;
}>;

export type TradeExecutionStatus = 'REJECTED' | 'CANCELLED' | 'FULLY_EXECUTED' | 'EXPIRED' | 'FAILED' | 'ACCEPTED';

export type TradeExecReport = Readonly<{
  quoteId: string;
  status: TradeExecutionStatus;
  bookingSkipped?: boolean;
  errors?: readonly NextWorkflowError[];
  execMessages?: readonly string[];
}>;

export type TradeInfos = Readonly<{
  technicalError?: ApiErrorMessage;
  tradeRejections?: readonly NextWorkflowError[];
  tradeAuthDurationInMillis?: number;
  tradeAuthStartTimeInMillis?: number;
  tradeAuthStatus?: NextTradeAuthorizationStatus;
  tradeAuthRejections?: readonly IRegulatoryRejection[];
  tradeStatus?: TradeStatus;
  comment: string;
  executionOnlyDealId: string;
  bookingSkipped?: boolean;
  tradeNotifications?: string[];
}>;

export const initialTradeInfos: TradeInfos = { comment: '', executionOnlyDealId: '' };

export type TradeInfosMap = Collection<TradeInfos>;
